import {
  createFrontendModule,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';

/**
 * Lazily loads the custom catalog page element. Extracted so the loader can
 * be exercised directly in unit tests.
 */
export const catalogPageLoader = () =>
  import('./CatalogPage').then(m => <m.CatalogPage />);

/**
 * Overrides the default `/catalog` page provided by
 * `@backstage/plugin-catalog/alpha` so its search box also matches an
 * entity's description, tags and owner, in addition to name/title
 * (JDP-1645).
 *
 * The module targets `pluginId: 'catalog'` and provides a page extension
 * with no name, which resolves to the id `page:catalog` - the same id as
 * the plugin's default page - so this custom page wins (see
 * `search/index.tsx` for the same pattern, already in use for JDP-1644).
 *
 * A full page replacement was needed rather than a smaller extension point:
 * the stock search box is hardcoded three layers deep inside
 * `@backstage/plugin-catalog` (`page:catalog` -> `NfsDefaultCatalogPage` ->
 * `CatalogTable` -> `OffsetPaginatedCatalogTable` ->
 * `components: { Toolbar: CatalogTableToolbar }` -> `<EntitySearchBar />`),
 * with no prop or blueprint input anywhere in that chain to override it.
 * Two narrower attempts both failed in production despite passing local
 * tests: intercepting and swapping the filter instance via a
 * `CatalogFilterBlueprint` contribution (raced against the search bar's own
 * state and lost every time), and monkey-patching
 * `EntityTextFilter.prototype` directly (this monorepo resolves
 * `@backstage/plugin-catalog-react` to two separate physical copies - one at
 * the repo root, one nested under `packages/app/node_modules` - so the
 * built-in search bar, living inside the root-resolved
 * `@backstage/plugin-catalog`, was never patching the same class object).
 *
 * This page's own `EntityListProvider`, `useEntityList`, `DefaultFilters`
 * and search bar are all imported from `packages/app/src`, so they
 * consistently resolve to the same copy of `@backstage/plugin-catalog-react`
 * and correctly share state with each other. The only stock catalog-plugin
 * piece reused is `CatalogTable.defaultColumnsFunc` for column rendering - a
 * pure function with no context dependency of its own, so which resolved
 * copy produced it doesn't matter.
 */
export const catalogPageModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    PageBlueprint.make({
      params: {
        path: '/catalog',
        loader: catalogPageLoader,
        routeRef: catalogPlugin.routes.catalogIndex,
      },
    }),
  ],
});
